import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Search, FileText, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      axios.get(`${API}/course/search?q=${encodeURIComponent(query)}`)
        .then(res => {
          setResults(res.data.results || []);
          setLoading(false);
        })
        .catch(() => {
          setResults([]);
          setLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = useCallback((result) => {
    onOpenChange(false);
    setQuery('');
    setResults([]);
    navigate(`/section/${result.section_id}`);
  }, [navigate, onOpenChange]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 overflow-hidden max-w-lg gap-0" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search Course Content</DialogTitle>
        <div className="flex items-center border-b border-border px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground mr-3 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search topics, sections, keywords..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            autoFocus
            data-testid="search-input"
          />
          <kbd className="hidden sm:inline-flex text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border ml-2">
            ESC
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto p-2">
          {query.length < 2 && (
            <p className="text-xs text-muted-foreground text-center py-8">
              Type at least 2 characters to search...
            </p>
          )}
          {query.length >= 2 && loading && (
            <p className="text-xs text-muted-foreground text-center py-8">Searching...</p>
          )}
          {query.length >= 2 && !loading && results.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-8" data-testid="search-no-results">
              No results found for "{query}"
            </p>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => handleSelect(r)}
              className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-secondary transition-colors flex items-start gap-3 group"
              data-testid={`search-result-${i}`}
            >
              <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {r.subtopic_title || r.section_title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Section {r.section_id}: {r.section_title}
                </p>
                {r.match && r.match !== r.subtopic_title && r.match !== r.section_title && (
                  <p className="text-xs text-muted-foreground/70 mt-0.5 truncate italic">
                    "{r.match}"
                  </p>
                )}
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
